import { FunctionComponent } from "react";

import {
	faArrowRight,
	faChevronDown,
	faPaperPlane,
	faReply,
} from "@fortawesome/pro-regular-svg-icons";
import { faCheck, faSparkles, faSpinner } from "@fortawesome/pro-solid-svg-icons";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";

import { classNames } from "@/utils/classNames";

import { ReachOutStatus } from "../../utils/columns/reachOutStatus";

interface ReachOutButtonProps {
	status: ReachOutStatus;
	/** Open the reach-out menu (default state) — anchored by the parent cell. */
	onReachOut: () => void;
	/** Jump straight to the generated drafts/snippets (ready state). */
	onReview: () => void;
	/** Open the reply thread (reply state — Assigned tab). */
	onReply: () => void;
	/**
	 * Keep the actionable button visible even when the row is not hovered — set while the menu is
	 * open or the lead's panel is active, so the trigger doesn't vanish mid-interaction.
	 */
	forceVisible?: boolean;
}

/** Fixed cell slot so channels don't shift as the button swaps between states. */
const SLOT = "flex h-7 w-[124px] items-center";

const PRIMARY_BTN =
	"flex h-7 w-[124px] cursor-pointer items-center justify-center gap-1.5 rounded-lg " +
	"bg-blue-action px-2 text-xs font-semibold text-white hover:bg-blue-action-dark";

const CHIP =
	"flex h-7 w-[124px] items-center justify-center gap-1.5 rounded-full px-2 text-xs font-semibold";

/**
 * The per-lead primary action in the Outreach column. Presentational: it renders exactly one of the
 * four {@link ReachOutStatus} states and delegates the click to the parent cell.
 *
 * Visibility mirrors the table row's `group` hover:
 * - `default` — hover-revealed "Reach out ▾" launcher (hidden on a resting row so the table stays calm).
 * - `ready`   — resting "AI drafts/snippets ✓" chip that swaps to a "Review →" button on row hover.
 * - `generating` — a persistent spinner chip (the action isn't ready, so hover changes nothing).
 * - `reply`   — a persistent "Reply" button (an outstanding reply is always worth surfacing).
 */
export const ReachOutButton: FunctionComponent<ReachOutButtonProps> = ({
	status,
	onReachOut,
	onReview,
	onReply,
	forceVisible = false,
}) => {
	const { t } = useTranslation();

	const stop = (fn: () => void) => (e: React.MouseEvent) => {
		e.stopPropagation();
		fn();
	};

	if (status.state === "reply") {
		return (
			<div className={SLOT}>
				<button
					aria-label={t("list.reachOut.reply")}
					className={PRIMARY_BTN}
					data-testid="reach-out-reply"
					type="button"
					onClick={stop(onReply)}
				>
					<Icon className="h-3 w-3" icon={faReply} />
					{t("list.reachOut.reply")}
				</button>
			</div>
		);
	}

	if (status.state === "generating") {
		const label =
			status.aiKind === "snippets" ? t("list.reachOut.aiSnippets") : t("list.reachOut.aiDrafts");

		return (
			<div className={SLOT}>
				<span
					className={classNames(CHIP, "bg-blue-action-200 text-blue-action")}
					data-testid="reach-out-generating"
				>
					<Icon className="h-3 w-3 animate-spin" icon={faSpinner} />
					{label}
				</span>
			</div>
		);
	}

	if (status.state === "ready") {
		const label =
			status.aiKind === "snippets" ? t("list.reachOut.aiSnippets") : t("list.reachOut.aiDrafts");

		return (
			<div className={SLOT}>
				{/* Resting: ready chip. Row hover (or forceVisible): swap to the Review action. */}
				<span
					className={classNames(
						CHIP,
						"bg-status-success-light text-status-success",
						forceVisible ? "hidden" : "flex group-hover:hidden"
					)}
					data-testid="reach-out-ready-chip"
				>
					<Icon className="h-3 w-3" icon={status.aiKind === "snippets" ? faSparkles : faCheck} />
					{label}
				</span>
				<button
					aria-label={t("list.reachOut.review")}
					className={classNames(PRIMARY_BTN, forceVisible ? "flex" : "hidden group-hover:flex")}
					data-testid="reach-out-review"
					type="button"
					onClick={stop(onReview)}
				>
					{t("list.reachOut.review")}
					<Icon className="h-3 w-3" icon={faArrowRight} />
				</button>
			</div>
		);
	}

	// default — the launcher. Hover-revealed so a resting table row shows only the channels.
	return (
		<div className={SLOT}>
			<button
				aria-label={t("list.reachOut.button")}
				className={classNames(PRIMARY_BTN, forceVisible ? "flex" : "hidden group-hover:flex")}
				data-testid="reach-out-launch"
				type="button"
				onClick={stop(onReachOut)}
			>
				<Icon className="h-3 w-3" icon={faPaperPlane} />
				{t("list.reachOut.button")}
				<Icon className="h-2.5 w-2.5 opacity-80" icon={faChevronDown} />
			</button>
		</div>
	);
};

/**
 * Primary Outreach action for a lead that already carries a `campaignId` (pre-assigned to a
 * campaign): jump to the surface where that lead's outreach actually lives — Outbox, Inbox or
 * Review-to-send, resolved by `resolveLeadReviewTarget` (PREC-4103), never a fixed page.
 * Rendered by the Outreach cell in place of the state-driven {@link ReachOutButton}. Opens in a new
 * tab so the list context is preserved, matching the other table-surface "review chat" links.
 */
export const ReviewToSendButton: FunctionComponent<{ href: string }> = ({ href }) => {
	const { t } = useTranslation();

	return (
		<div className={SLOT}>
			<a
				aria-label={t("list.reachOut.review")}
				className={PRIMARY_BTN}
				data-testid="reach-out-review-to-send"
				href={href}
				rel="noreferrer"
				target="_blank"
				onClick={(e) => e.stopPropagation()}
			>
				{t("list.reachOut.review")}
				<Icon className="h-3 w-3" icon={faArrowRight} />
			</a>
		</div>
	);
};
